import { cn } from '@/lib/utils';

interface GlobeSpinnerProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

export function GlobeSpinner({ className, size = 'md', label = 'Loading data...' }: GlobeSpinnerProps) {
  const sizeMap = { sm: 48, md: 80, lg: 120 };
  const s = sizeMap[size];

  return (
    <div className={cn('flex flex-col items-center justify-center gap-4 py-12', className)}>
      <div className="relative" style={{ width: s, height: s }}>
        {/* Outer glow */}
        <div
          className="absolute inset-0 rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, oklch(55% 0.18 250 / 0.2) 0%, transparent 70%)',
          }}
        />
        {/* Globe SVG */}
        <svg
          viewBox="0 0 100 100"
          width={s}
          height={s}
          className="globe-spin"
        >
          {/* Ocean */}
          <circle
            cx="50" cy="50" r="44"
            fill="none"
            stroke="url(#globeGradient)"
            strokeWidth="2"
          />
          <circle
            cx="50" cy="50" r="44"
            fill="oklch(55% 0.18 250 / 0.08)"
          />

          {/* Longitude lines that rotate */}
          <g className="globe-lines">
            <ellipse cx="50" cy="50" rx="44" ry="44" fill="none" stroke="oklch(55% 0.18 250 / 0.3)" strokeWidth="0.8" />
            <ellipse cx="50" cy="50" rx="22" ry="44" fill="none" stroke="oklch(55% 0.18 250 / 0.3)" strokeWidth="0.8" />
            <ellipse cx="50" cy="50" rx="36" ry="44" fill="none" stroke="oklch(55% 0.18 250 / 0.2)" strokeWidth="0.6" />
            <ellipse cx="50" cy="50" rx="10" ry="44" fill="none" stroke="oklch(55% 0.18 250 / 0.2)" strokeWidth="0.6" />
          </g>

          {/* Latitude lines (static) */}
          <line x1="6" y1="30" x2="94" y2="30" stroke="oklch(55% 0.18 250 / 0.2)" strokeWidth="0.6" />
          <line x1="6" y1="50" x2="94" y2="50" stroke="oklch(55% 0.18 250 / 0.3)" strokeWidth="0.8" />
          <line x1="6" y1="70" x2="94" y2="70" stroke="oklch(55% 0.18 250 / 0.2)" strokeWidth="0.6" />

          {/* Simplified continent shapes - Americas side */}
          <g className="globe-continents">
            {/* North America */}
            <path
              d="M30,25 Q35,22 40,24 Q44,26 42,32 Q40,36 36,38 Q32,37 30,34 Q28,30 30,25Z"
              fill="oklch(75% 0.14 195 / 0.4)"
              stroke="oklch(75% 0.14 195 / 0.6)"
              strokeWidth="0.5"
            />
            {/* South America */}
            <path
              d="M38,48 Q42,46 44,50 Q45,56 43,62 Q40,66 37,64 Q34,60 35,54 Q36,50 38,48Z"
              fill="oklch(75% 0.14 195 / 0.35)"
              stroke="oklch(75% 0.14 195 / 0.5)"
              strokeWidth="0.5"
            />
            {/* Europe/Africa */}
            <path
              d="M54,26 Q58,24 62,27 Q64,30 62,34 Q60,32 56,33 Q54,30 54,26Z"
              fill="oklch(75% 0.14 195 / 0.35)"
              stroke="oklch(75% 0.14 195 / 0.5)"
              strokeWidth="0.5"
            />
            <path
              d="M56,40 Q60,38 62,42 Q63,48 61,54 Q58,58 55,55 Q53,50 54,44 Q55,42 56,40Z"
              fill="oklch(75% 0.14 195 / 0.3)"
              stroke="oklch(75% 0.14 195 / 0.45)"
              strokeWidth="0.5"
            />
            {/* Asia */}
            <path
              d="M64,24 Q70,22 74,26 Q76,30 74,34 Q70,36 66,33 Q63,28 64,24Z"
              fill="oklch(75% 0.14 195 / 0.3)"
              stroke="oklch(75% 0.14 195 / 0.45)"
              strokeWidth="0.5"
            />
          </g>

          {/* Data points pulsing */}
          <circle cx="36" cy="30" r="1.5" fill="oklch(75% 0.14 195)" className="globe-dot-1" />
          <circle cx="58" cy="28" r="1.5" fill="oklch(75% 0.14 195)" className="globe-dot-2" />
          <circle cx="70" cy="28" r="1.5" fill="oklch(75% 0.14 195)" className="globe-dot-3" />
          <circle cx="42" cy="54" r="1.2" fill="oklch(75% 0.14 195)" className="globe-dot-1" />
          <circle cx="60" cy="48" r="1.2" fill="oklch(75% 0.14 195)" className="globe-dot-2" />

          {/* Outer ring */}
          <circle
            cx="50" cy="50" r="46"
            fill="none"
            stroke="url(#globeGradient)"
            strokeWidth="1.5"
            strokeDasharray="4 6"
            className="globe-ring"
          />

          <defs>
            <linearGradient id="globeGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="oklch(55% 0.18 250)" />
              <stop offset="100%" stopColor="oklch(75% 0.14 195)" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      {label && (
        <p className="text-sm text-muted-foreground animate-pulse">{label}</p>
      )}
    </div>
  );
}
